import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useTheme } from "@emotion/react";
import useThemeColor from "../../utils/useTheme";
import {
  Bookmark,
  BookmarkBorder,
  Lock,
  PlayArrow,
  PlayCircleFilled,
  Toc
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
  updateTrainingById,
  updateTrainingProgressById
} from "../../features/training/trainingSlice";
import { updateTrainingProgress } from "../../api/trainingApi";

const TocComponent = ({
  trainingWithTitleData,
  handleTocItemClick,
  sx,
  styles
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (training) => {
    handleTocItemClick(training);
    handleClose();
  };

  return (
    <>
      <Chip
        icon={<Toc sx={{ fontSize: 16 }} />}
        label="ToC"
        onClick={handleClick}
        variant="outlined"
        size="small"
        sx={{
          ...sx,
          height: 28,
          fontSize: "11px",
          fontWeight: 500,
          color: styles.color,
          borderColor: styles.lightGreyColor,
          backgroundColor: styles.background,
          "&:hover": {
            borderColor: styles.dayHighlighter,
            backgroundColor: `${styles.dayHighlighter}10`
          },
          "& .MuiChip-icon": {
            color: styles.color,
            marginLeft: "4px"
          }
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        PaperProps={{
          sx: {
            bgcolor: styles.background,
            border: `1px solid ${styles.lightGreyColor}`,
            borderRadius: 2,
            maxHeight: 300,
            minWidth: 200,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            "& .MuiMenuItem-root": {
              fontSize: "12px",
              minHeight: "auto",
              padding: 0
            }
          }
        }}
      >
        {trainingWithTitleData.trainings?.map((training) => (
          <MenuItem
            key={training.trainingId}
            onClick={() => handleItemClick(training)}
            sx={{
              padding: 0,
              "&:hover": {
                backgroundColor: `${styles.dayHighlighter}15`
              }
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                borderBottom: `1px solid ${styles.lightGreyColor}40`,
                width: "100% !important",
                "&:last-child": {
                  borderBottom: "none"
                }
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    color: styles.color,
                    fontSize: "12px",
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  <span
                    style={{
                      color: "#64AEDD",
                      fontWeight: 600
                    }}
                  >
                    {training.day}
                  </span>{" "}
                  <span
                    style={{
                      fontStyle: "italic"
                    }}
                  >
                    {training.title}
                  </span>
                </Typography>
              </Box>
              <Box
                sx={{ ml: 1, display: "flex", gap: 2, alignItems: "center" }}
              >
                <IconButton
                  sx={{ padding: "0 !important" }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    dispatch(
                      updateTrainingProgressById({
                        id: training.trainingId,
                        updates: { isFavorite: !training.isFavorite }
                      })
                    );
                    dispatch(
                      updateTrainingById({
                        id: training.trainingId,
                        updates: { isFavorite: !training.isFavorite }
                      })
                    );
                    await updateTrainingProgress({
                      trainingId: training?.trainingId,
                      isFavorite: !training?.isFavorite
                    });
                  }}
                >
                  {training?.isFavorite ? (
                    <Bookmark
                      sx={{
                        height: "18px",
                        width: "18px",
                        fill: "#03B55C"
                      }}
                    />
                  ) : (
                    <BookmarkBorder
                      sx={{
                        height: "18px",
                        width: "18px",
                        fill: styles.color
                      }}
                    />
                  )}
                </IconButton>
                {training.isUnlocked ? (
                  <PlayCircleFilled sx={{ color: "#64AEDD", fontSize: 16 }} />
                ) : (
                  <Lock sx={{ color: "#9E9E9E", fontSize: 14 }} />
                )}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const DaySelector = ({
  activeDay,
  setActiveDay,
  startDay,
  setStartDay,
  trainingData,
  allCompletedData,
  handleDayClick,
  handleNext,
  handlePrevious,
  visibleDays,
  visibleDaysCount,
  days,
  trainingWithTitleData,
  enabledDays = []
}) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLaptop = useMediaQuery("(min-width: 900px) and (max-width: 1300px)");
  const isBiggerLaptop = useMediaQuery(
    "(min-width: 900px) and (max-width: 1500px)"
  );
  const styles = useThemeColor();
  const [tocOpen, setTocOpen] = useState(false);

  const handleTocChange = (event) => {
    const selectedValue = event.target.value;
    setTocOpen(false);
    // selectedValue will be empty string when placeholder is selected
    if (selectedValue === "") return;

    // Handle the navigation logic here
    // You can implement your own logic as mentioned
    console.log("Selected training:", selectedValue);
  };

  const handleTocItemClick = (training) => {
    const { trainingId, day, title, isUnlocked } = training;

    if (!isUnlocked) return;

    const dayNumber = +day?.split(" ")[1];
    setActiveDay(dayNumber);

    // Your localStorage logic
    localStorage.setItem(
      "lastProgramVisitedLevel",
      JSON.stringify({
        level: trainingWithTitleData?.levelName,
        activeDay: dayNumber
      })
    );

    const pageIndex = Math.floor((dayNumber - 1) / visibleDaysCount);
    const calculatedStartDay = pageIndex * visibleDaysCount + 1;
    setStartDay(calculatedStartDay);

    setTocOpen(false);
  };

  return (
    <>
      {(isMobile || isBiggerLaptop) && trainingWithTitleData && (
        <TocComponent
          sx={{
            display: "flex",
            margin: "0 auto",
            justifyContent: "center",
            marginTop: isBiggerLaptop ? 0 : "-20px"
          }}
          trainingWithTitleData={trainingWithTitleData}
          handleTocItemClick={handleTocItemClick}
          styles={styles}
        />
      )}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderBottom={`1px solid ${styles.lightGreyColor}`}
        sx={{
          backgroundColor: styles.background,
          padding: { md: "0 0px 10px 0", lg: "0 10px 20px 10px" },
          gap: isLaptop ? "22px" : { md: "4px", lg: "30px" },
          ...(isMobile && { gap: "4px", paddingBottom: "10px" })
        }}
      >
        <Snackbar
          open={openSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarMessage.includes("Success") ? "success" : "error"}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        {!isMobile && !isBiggerLaptop && trainingWithTitleData && (
          <TocComponent
            trainingWithTitleData={trainingWithTitleData}
            handleTocItemClick={handleTocItemClick}
            styles={styles}
          />
        )}
        <IconButton
          sx={{ color: styles.color, p: { xs: 0, md: 0, lg: 1 } }}
          aria-label="previous"
          onClick={handlePrevious}
          disabled={startDay === 1}
        >
          <ArrowBackIosIcon />
        </IconButton>
        {visibleDays.map((day) => {
          const isEnabled = enabledDays.includes(day); // Check if the day is in the enabledDays list

          return (
            <Box
              display="flex"
              onClick={() => {
                if (isEnabled) {
                  handleDayClick(day);
                } else {
                  setSnackbarMessage("1 day of training unlocks each day.");
                  setOpenSnackbar(true);
                }
              }} // Only handle click if day is enabled
              alignItems="center"
              textAlign="center"
              flexDirection="column"
              gap={1}
              borderRadius={100}
              height={80}
              sx={{
                cursor: isEnabled ? "pointer" : "not-allowed", // Show "not-allowed" cursor for disabled days
                padding: isLaptop
                  ? "8px 3px"
                  : { sm: "5px", lg: "8px", md: "8px 3px" },
                ...(isMobile && { padding: "5px" }),
                color: activeDay === day ? "white" : "#9e9e9e",
                backgroundColor:
                  activeDay === day ? styles.dayHighlighter : "transparent",
                opacity: isEnabled ? 1 : 0.5 // Lower opacity for disabled days
              }}
              key={day}
            >
              <Typography
                color={activeDay === day ? styles.color : styles.dayViewColor}
                fontSize="13px"
              >
                Day
              </Typography>
              <Button
                onClick={() => isEnabled && handleDayClick(day)}
                variant={activeDay === day ? "contained" : "text"}
                sx={{
                  ":disabled": {
                    color: styles.disabledColor
                  },
                  ...(allCompletedData?.includes(day) && {
                    border: "2px solid #368AD0"
                  }),
                  color: activeDay === day ? "#FCFCFC" : styles.disabledColor,
                  backgroundColor:
                    activeDay === day ? "#368AD0" : "transparent",
                  minWidth: "40px",
                  borderRadius: "50%",
                  padding: "6px"
                }}
                disabled={!isEnabled} // Disable button for days not in enabledDays
              >
                {day}
              </Button>
            </Box>
          );
        })}
        <IconButton
          sx={{ color: styles.color, p: { xs: 0, md: 0, lg: 1 } }}
          aria-label="next"
          onClick={handleNext}
          disabled={startDay + visibleDaysCount - 1 >= days.length}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>
    </>
  );
};

export default DaySelector;
